'use client'

import Link from 'next/link'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { trackCtaClick, buildTrackedCTAUrl } from '@/lib/cta-tracking'
import { accountUrl } from '@/lib/config'
import { usePathname } from 'next/navigation'
import { businessMarketingFeatures, personalMarketingFeatures } from '@/lib/marketing-features'

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [personalOpen, setPersonalOpen] = useState(false)
  const [businessOpen, setBusinessOpen] = useState(false)
  const [isHidden, setIsHidden] = useState(false)
  const pathname = usePathname()
  const menuCloseTimeoutRef = useRef<number | null>(null)
  const isBusinessRoute = pathname === '/business' || pathname.startsWith('/business/')
  const loginUrl = `${accountUrl}${isBusinessRoute ? '/app/business/login' : '/app/auth/login'}`
  const registerUrl = `${accountUrl}${isBusinessRoute ? '/app/business/signup' : '/app/auth/register'}`

  const getHeaderPageContext = () => {
    const slug = pathname === '/' ? 'home' : pathname.replace(/^\//, '').replace(/\/$/, '')
    return { page_type: 'header' as const, page_name: 'Header', page_slug: slug, page_category: 'navigation' }
  }
  const [lastScrollY, setLastScrollY] = useState(0)

  const closeDesktopMenus = () => {
    setPersonalOpen(false)
    setBusinessOpen(false)
  }

  const clearMenuCloseTimer = () => {
    if (menuCloseTimeoutRef.current) {
      clearTimeout(menuCloseTimeoutRef.current)
      menuCloseTimeoutRef.current = null
    }
  }

  const scheduleMenuClose = (setter: (value: boolean) => void) => {
    clearMenuCloseTimer()
    menuCloseTimeoutRef.current = window.setTimeout(() => setter(false), 180)
  }

  const occasions = [
    {
      title: "Anniversary",
      href: "/personal/occasions/anniversary",
      icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" /></svg>
    },
    {
      title: "Baby Shower",
      href: "/personal/occasions/baby-shower",
      icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
    },
    {
      title: "Birthday",
      href: "/personal/occasions/birthday",
      icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" /></svg>
    },
    {
      title: "Engagement",
      href: "/personal/occasions/engagement",
      icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
    },
    {
      title: "Farewell",
      href: "/personal/occasions/farewell",
      icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" /></svg>
    },
    {
      title: "Father's Day",
      href: "/personal/occasions/fathers-day",
      icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
    },
    {
      title: "Gender Reveal",
      href: "/personal/occasions/gender-reveal",
      icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 1.343-3 3s1.343 3 3 3 3-1.343 3-3-1.343-3-3-3zM12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2z" /></svg>
    },
    {
      title: "Get Well",
      href: "/personal/occasions/get-well",
      icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-3-3v6M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
    },
    {
      title: "Graduation",
      href: "/personal/occasions/graduation",
      icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" /></svg>
    },
    {
      title: "Holiday",
      href: "/personal/occasions/holiday",
      icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
    },
    {
      title: "Memorial",
      href: "/personal/occasions/memorial",
      icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
    },
    {
      title: "Mother's Day",
      href: "/personal/occasions/mothers-day",
      icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
    },
    {
      title: "Promotion",
      href: "/personal/occasions/promotion",
      icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
    },
    {
      title: "Retirement",
      href: "/personal/occasions/retirement",
      icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
    },
    {
      title: "Thank You",
      href: "/personal/occasions/thank-you",
      icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" /></svg>
    },
    {
      title: "Valentine's Day",
      href: "/personal/occasions/valentines-day",
      icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
    },
    {
      title: "Wedding",
      href: "/personal/occasions/wedding",
      icon: <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
    }
  ]


  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const isScrollingDown = currentScrollY > lastScrollY
      const shouldHide = isScrollingDown && currentScrollY > 80

      setIsHidden(shouldHide)
      setLastScrollY(currentScrollY)
      
      // Close mobile menu when scrolling
      if (mobileMenuOpen && currentScrollY > lastScrollY) {
        setMobileMenuOpen(false)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY, mobileMenuOpen])

  return (
    <header className={`border-b border-gray-200 bg-white fixed top-0 w-full z-50 transition-all duration-300 ${isHidden ? '-translate-y-full' : 'translate-y-0'} ${lastScrollY > 0 ? 'shadow-md' : ''}`}>
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
        {/* Logo */}
        <Link href="/" className="flex items-center">
          <Image
            src="/images/brand-logo/vibevite-logo-500x200.webp"
            alt="VibeVite"
            width={200}
            height={80}
            className="h-14 w-auto md:h-16"
            unoptimized
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden items-center gap-8 md:flex">
          <div
            className="relative"
            onMouseEnter={() => {
              clearMenuCloseTimer()
              setPersonalOpen(true)
              setBusinessOpen(false)
            }}
            onMouseLeave={() => scheduleMenuClose(setPersonalOpen)}
          >
            <div className="flex items-center gap-1 py-2 text-[#2B0A4A]">
              <Link href="/personal" className="transition-colors hover:text-[#FF8A3D]">
                Personal
              </Link>
              <button
                type="button"
                aria-label="Toggle Personal menu"
                aria-expanded={personalOpen}
                className="transition-colors hover:text-[#FF8A3D]"
                onClick={() => {
                  clearMenuCloseTimer()
                  setPersonalOpen((open) => !open)
                  setBusinessOpen(false)
                }}
              >
                <svg className={`h-4 w-4 transition-transform ${personalOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            {personalOpen && (
              <div className="absolute left-1/2 top-full z-50 mt-3 w-[min(980px,calc(100vw-48px))] -translate-x-1/2 rounded-2xl border border-gray-100 bg-white p-6 shadow-2xl">
                <div>
                  <div className="mb-4 flex items-center justify-between border-b border-gray-100 pb-4">
                    <div>
                      <h3 className="text-base font-semibold text-[#2B0A4A]">Personal Events</h3>
                      <p className="text-xs text-[#2B0A4A]/60">Plan, share, and preserve life&apos;s moments</p>
                    </div>
                    <Link href="/features#personal-features" className="text-xs font-semibold text-[#FF8A3D] hover:underline" onClick={closeDesktopMenus}>
                      All Personal Features
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {personalMarketingFeatures.map((feature) => (
                      <Link
                        key={feature.slug}
                        href={feature.href}
                        className="group flex items-start gap-3 rounded-lg border border-gray-100 p-3 transition-all hover:border-[#FFC933] hover:bg-[#FFF8EC] hover:shadow-md"
                        onClick={closeDesktopMenus}
                      >
                        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#F1E9FF] text-lg text-[#2B0A4A]">
                          {feature.icon}
                        </span>
                        <span>
                          <span className="block text-sm font-medium text-[#2B0A4A] group-hover:text-[#FF8A3D]">{feature.title}</span>
                          <span className="mt-0.5 block text-xs leading-relaxed text-[#2B0A4A]/60">{feature.menuDescription}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div
            className="relative"
            onMouseEnter={() => {
              clearMenuCloseTimer()
              setBusinessOpen(true)
              setPersonalOpen(false)
            }}
            onMouseLeave={() => scheduleMenuClose(setBusinessOpen)}
          >
            <div className="flex items-center gap-1 py-2 text-[#2B0A4A]">
              <Link href="/business" className="transition-colors hover:text-[#FF8A3D]">
                Business
              </Link>
              <button
                type="button"
                aria-label="Toggle Business menu"
                aria-expanded={businessOpen}
                className="transition-colors hover:text-[#FF8A3D]"
                onClick={() => {
                  clearMenuCloseTimer()
                  setBusinessOpen((open) => !open)
                  setPersonalOpen(false)
                }}
              >
                <svg className={`h-4 w-4 transition-transform ${businessOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            {businessOpen && (
              <div className="absolute left-1/2 top-full z-50 mt-3 w-[min(980px,calc(100vw-48px))] -translate-x-1/2 rounded-2xl border border-gray-100 bg-white p-6 shadow-2xl">
                <div>
                  <div className="mb-4 flex items-center justify-between border-b border-gray-100 pb-4">
                    <div>
                      <h3 className="text-base font-semibold text-[#2B0A4A]">Business Walls</h3>
                      <p className="text-xs text-[#2B0A4A]/60">Recognition built for teams and organizations</p>
                    </div>
                    <Link href="/features#business-features" className="text-xs font-semibold text-[#FF8A3D] hover:underline" onClick={closeDesktopMenus}>
                      All Business Features
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {businessMarketingFeatures
                      .filter((feature) => !['company-branding', 'engagement-analytics'].includes(feature.slug))
                      .map((feature) => (
                      <Link
                        key={feature.slug}
                        href={feature.href}
                        className="group flex items-start gap-3 rounded-lg border border-gray-100 p-3 transition-all hover:border-[#FFC933] hover:bg-[#FFF8EC] hover:shadow-md"
                        onClick={closeDesktopMenus}
                      >
                        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#FFF8EC] text-lg text-[#2B0A4A]">
                          {feature.icon}
                        </span>
                        <span>
                          <span className="block text-sm font-medium text-[#2B0A4A] group-hover:text-[#FF8A3D]">{feature.title}</span>
                          <span className="mt-0.5 block text-xs leading-relaxed text-[#2B0A4A]/60">{feature.menuDescription}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          <Link href="/pricing" className="text-[#2B0A4A] transition-colors hover:text-[#FF8A3D]">
            Pricing
          </Link>
        </nav>

        {/* Desktop Buttons */}
        <div className="hidden items-center gap-4 md:flex">
          <a
            href={loginUrl}
            onClick={(e) => { e.preventDefault(); trackCtaClick(getHeaderPageContext(), { cta_name: 'login', cta_position: 'header', cta_variant: 'secondary' }); window.location.href = buildTrackedCTAUrl(loginUrl, getHeaderPageContext(), { cta_name: 'login', cta_position: 'header', cta_variant: 'secondary' }) }}
            className="px-3 py-3 text-base font-semibold text-[#1B0044] transition-colors hover:text-[#2B0A4A]"
          >
            Log in
          </a>
          <a
            href={registerUrl}
            onClick={(e) => { e.preventDefault(); trackCtaClick(getHeaderPageContext(), { cta_name: 'get_started', cta_position: 'header', cta_variant: 'primary' }); window.location.href = buildTrackedCTAUrl(registerUrl, getHeaderPageContext(), { cta_name: 'get_started', cta_position: 'header', cta_variant: 'primary' }) }}
            className="inline-flex min-h-12 items-center justify-center rounded-full bg-[#FFC933] px-8 text-base font-semibold text-[#1B0044] transition-colors hover:bg-[#FFBF14]"
          >
            Get Started
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-[#2B0A4A]"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="absolute left-0 right-0 top-full border-t border-gray-200 bg-white px-6 py-4 shadow-lg md:hidden max-h-[calc(100vh-5rem)] overflow-y-auto">
          <nav className="flex flex-col gap-4">
            <Link
              href="/personal"
              className="text-[#2B0A4A] transition-colors hover:text-[#FF8A3D]"
              onClick={() => setMobileMenuOpen(false)}
            >
              Personal
            </Link>
            <Link
              href="/business"
              className="text-[#2B0A4A] transition-colors hover:text-[#FF8A3D]"
              onClick={() => setMobileMenuOpen(false)}
            >
              Business
            </Link>
            <Link
              href="/pricing"
              className="text-[#2B0A4A] transition-colors hover:text-[#FF8A3D]"
              onClick={() => setMobileMenuOpen(false)}
            >
              Pricing
            </Link>
            <a
              href={loginUrl}
              className="px-2 py-2 text-center text-base font-semibold text-[#1B0044] transition-colors hover:text-[#2B0A4A]"
              onClick={(e) => { e.preventDefault(); trackCtaClick(getHeaderPageContext(), { cta_name: 'login', cta_position: 'header_mobile', cta_variant: 'secondary' }); setMobileMenuOpen(false); window.location.href = buildTrackedCTAUrl(loginUrl, getHeaderPageContext(), { cta_name: 'login', cta_position: 'header_mobile', cta_variant: 'secondary' }) }}
            >
              Log in
            </a>
            <a
              href={registerUrl}
              className="inline-flex min-h-12 items-center justify-center rounded-full bg-[#FFC933] px-6 text-center text-base font-semibold text-[#1B0044] transition-colors hover:bg-[#FFBF14]"
              onClick={(e) => { e.preventDefault(); trackCtaClick(getHeaderPageContext(), { cta_name: 'get_started', cta_position: 'header_mobile', cta_variant: 'primary' }); setMobileMenuOpen(false); window.location.href = buildTrackedCTAUrl(registerUrl, getHeaderPageContext(), { cta_name: 'get_started', cta_position: 'header_mobile', cta_variant: 'primary' }) }}
            >
              Get Started
            </a>
          </nav>
        </div>
      )}
    </header>
  )
}
